import type { ConnectedUser, FeedsClient } from '@stream-io/feeds-client'
import type { StreamChat } from 'stream-chat'

import {
    CATEGORY_ORDER,
    DEFAULT_NOTIFICATION_CATEGORY_LEVELS,
    NotificationCategoryLevels,
    NotificationLevel,
} from '@/types'

import { isRecord } from './isRecord'
import { createLocalStorageItem, LocalStorageItem, parseBoolean } from './localStorageItem'
import { getInAppEnabled, setInAppEnabled } from './notificationCategoryPreferences'
import { buildChatPushPreference, buildFeedsPushPreference, derivePushEnabledFromResponses } from './pushPreferences'

const CACHED_LEVELS_STORAGE_KEY_PREFIX = 'valk-talk-notification-category-levels'

export const isNotificationLevel = (value: unknown): value is NotificationLevel => (
    typeof value === 'string' && Object.values<string>(NotificationLevel).includes(value)
)

const isNotificationCategoryLevels = (value: unknown): value is NotificationCategoryLevels =>
    isRecord(value) && CATEGORY_ORDER.every(category => isNotificationLevel(value[category]))


const cachedLevelsStorage = (userId: string): LocalStorageItem<NotificationCategoryLevels | null> => createLocalStorageItem<NotificationCategoryLevels | null>({
    key: `${CACHED_LEVELS_STORAGE_KEY_PREFIX}:${userId}`,
    fallback: null,
    parse: (raw) => {
        const parsed: unknown = JSON.parse(raw)

        return isNotificationCategoryLevels(parsed) ? parsed : null
    },
    serialize: JSON.stringify,
})

// Only the parts of the Stream clients that are used here, so tests can pass small fakes
export type ChatPushPreferencesClient = Pick<StreamChat, 'user' | 'setPushPreferences'>
export type FeedsPushPreferencesClient = Pick<FeedsClient, 'updatePushNotificationPreferences'>

export const deriveNotificationCategoryLevels = (
    chatClient: ChatPushPreferencesClient | undefined,
    connectedFeedsUser: ConnectedUser | undefined,
    userId: string,
): NotificationCategoryLevels | null => {
    if (! chatClient) {
        return null
    }

    const chatUser = chatClient.user
    const chatPushPreferences = chatUser && 'push_preferences' in chatUser ? chatUser.push_preferences : undefined
    const feedsPushPreferences = connectedFeedsUser?.push_preferences

    const cached = cachedLevelsStorage(userId).get()

    if (cached) {
        return cached
    }

    const pushEnabled = derivePushEnabledFromResponses(chatPushPreferences, feedsPushPreferences)

    return CATEGORY_ORDER.reduce<NotificationCategoryLevels>((accumulator, category) => {
        const inAppEnabled = getInAppEnabled(category, userId)

        accumulator[category] = pushEnabled[category]
            ? NotificationLevel.pushAndInApp
            : (inAppEnabled ? NotificationLevel.inAppOnly : NotificationLevel.off)

        return accumulator
    }, { ...DEFAULT_NOTIFICATION_CATEGORY_LEVELS })
}

export const saveNotificationCategoryLevels = async (
    chatClient: ChatPushPreferencesClient,
    feedsClient: FeedsPushPreferencesClient,
    userId: string,
    levels: NotificationCategoryLevels,
): Promise<void> => {
    CATEGORY_ORDER.forEach((category) => {
        setInAppEnabled(category, userId, levels[category] !== NotificationLevel.off)
    })

    await Promise.all([
        chatClient.setPushPreferences([buildChatPushPreference(levels)]),
        feedsClient.updatePushNotificationPreferences({ preferences: [buildFeedsPushPreference(levels)] }),
    ])

    cachedLevelsStorage(userId).set(levels)
}

const DEFAULTS_APPLIED_STORAGE_KEY_PREFIX = 'valk-talk-notification-defaults-applied'

const defaultsAppliedStorage = (userId: string): LocalStorageItem<boolean> => createLocalStorageItem({
    key: `${DEFAULTS_APPLIED_STORAGE_KEY_PREFIX}:${userId}`,
    fallback: false,
    parse: parseBoolean,
})

export const hasAppliedDefaultCategoryLevels = (userId: string): boolean => defaultsAppliedStorage(userId).get()

export const markDefaultCategoryLevelsApplied = (userId: string): void => {
    defaultsAppliedStorage(userId).set(true)
}
